import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useState } from 'react';
import { Device } from '../../../store/reducers/deviceReducers';
import deviceApi, { RecalibrationResult } from '../../../api/devices';

const errorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  error?.message ||
  'Something went wrong. Please try again.';

const formatFormula = (formula: { converter: number; constant: number; cable_length: number }) =>
  `Y = X * ${formula.converter} + ${formula.constant} - ${formula.cable_length}`;

const DeviceCalibration = ({
  device,
  disabled,
  onFormulaReady,
}: {
  device: Device;
  disabled?: boolean;
  onFormulaReady?: (result: RecalibrationResult) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [wellId, setWellId] = useState('');
  const [address, setAddress] = useState('');
  const [days, setDays] = useState(365);
  const [preview, setPreview] = useState<RecalibrationResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const wellPayload = () => ({
    mode: 'well',
    ...(wellId.trim() ? { well_id: wellId.trim() } : {}),
    ...(address.trim() ? { address: address.trim() } : {}),
    days,
  });

  const loadPreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const response = await deviceApi.recalibrateDevice(device.serial, wellPayload())();
      setPreview(response.data);
    } catch (err) {
      setPreviewError(errorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const openDialog = () => {
    setOpen(true);
    loadPreview();
  };

  const useFormula = () => {
    if (!preview) {
      return;
    }
    onFormulaReady?.(preview);
    setOpen(false);
    setPreview(null);
  };

  return (
    <>
      <Tooltip title="Calculate a formula from this well's BGwT readings and fill the fields. Submit to save it.">
        <span>
          <Button
            size='small'
            variant='outlined'
            startIcon={<AutorenewIcon />}
            disabled={disabled}
            onClick={openDialog}
          >
            Recalibrate
          </Button>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={() => !previewLoading && setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Recalibrate from well data</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <DialogContentText>
              The cable length is fitted to this well&apos;s BGwT manual readings. The target name
              is used as the well address unless you override it. Using the result fills the formula
              fields for {device.serial}. Nothing is written until you submit.
            </DialogContentText>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size='small'
                  label='Well ID'
                  placeholder='Match by address'
                  value={wellId}
                  onChange={(event) => setWellId(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size='small'
                  label='Address override'
                  placeholder={device.target?.name || 'Well address'}
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size='small'
                  label='Lookback'
                  type='number'
                  value={days}
                  onChange={(event) => setDays(Number(event.target.value))}
                  inputProps={{ min: 7, max: 365 }}
                />
              </Grid>
            </Grid>
            <Box>
              <Button size='small' variant='text' onClick={loadPreview} disabled={previewLoading}>
                Refresh preview
              </Button>
            </Box>

            {previewLoading && (
              <Box display='flex' alignItems='center' gap={1}>
                <CircularProgress size={20} />
                <Typography variant='body2' color='text.secondary'>
                  Fitting cable length to well readings…
                </Typography>
              </Box>
            )}
            {previewError && <Alert severity='error'>{previewError}</Alert>}
            {preview && (
              <Alert severity={preview.warning ? 'warning' : 'info'}>
                <Typography variant='body2'>
                  {preview.warning ||
                    'Derived formula. Using it fills the fields below. Nothing is saved until you submit.'}
                </Typography>
                <Typography variant='body2' fontFamily="'Courier New', monospace" sx={{ mt: 1 }}>
                  {formatFormula(preview)}
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={previewLoading}>
            Cancel
          </Button>
          <Button
            variant='contained'
            startIcon={<AutorenewIcon />}
            onClick={useFormula}
            disabled={previewLoading || !preview}
          >
            Use this formula
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeviceCalibration;
